import { Component, inject, OnInit, OnDestroy, signal, computed, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil, take } from 'rxjs';

import { environment } from '../../../environments/environment';

import { WorkflowRequestsService } from '../../service/entity-services/workflow-requests.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { PartyService } from '../../service/entity-services/party.service';
import { LegalEntityService } from '../../service/entity-services/legal-entity.service';
import { SiteService } from '../../service/entity-services/site.service';
import { AuthService } from '../../service/auth.service';
import { EntityActionsSidenavComponent, EntityAction } from '../../entity-form-components/entity-actions-sidenav/entity-actions-sidenav.component';
import {
  WorkflowRequest,
  WORKFLOW_PHASES,
  nextPhase as computeNextPhase,
  previousPhase as computePreviousPhase
} from '../../objects/workflow';
import { NewWorkflowRequestDialogComponent } from '../../shared/components/new-workflow-request-dialog/new-workflow-request-dialog.component';
import { ProvisionReviewTableComponent } from '../../shared/components/provision-review-table/provision-review-table.component';
import { ApprovedProvisionsService } from '../../service/ai/approved-provisions.service';
import { ProvisionAnalysisService } from '../../service/ai/provision-analysis.service';
import { ProvisionAnalysis, ProvisionComparison, ProvisionAnalysisResponse } from '../../objects/provision-analysis';
import { Provision } from '../../objects/provision';

interface MatchedEntity {
  id: string;
  name: string;
  route: string;   // navigation target when the link is clicked
}

interface AiTableRow {
  label: string;
  value: string;
}

@Component({
  selector: 'app-workflow-item',
  templateUrl: './workflow-item.component.html',
  styleUrls: ['./workflow-item.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatTableModule,
    DatePipe,
    EntityActionsSidenavComponent,
    ProvisionReviewTableComponent
  ]
})
export class WorkflowItemComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  public router = inject(Router);
  private workflowService = inject(WorkflowRequestsService);
  private sidenavContentService = inject(SidenavContentService);
  private partyService = inject(PartyService);
  private legalEntityService = inject(LegalEntityService);
  private siteService = inject(SiteService);
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private approvedProvisionsService = inject(ApprovedProvisionsService);
  private provisionAnalysisService = inject(ProvisionAnalysisService);
  private destroy$ = new Subject<void>();

  /** Signed-in user, stamped into phaseHistory.enteredBy on Advance / Return. */
  private currentUserEmail = '';

  @ViewChild('actionsSidenav') actionsSidenav!: MatSidenav;

  // Core state as signals so dependent computed signals stay in sync
  request = signal<WorkflowRequest | null>(null);
  loading = signal<boolean>(true);
  error = signal<string | null>(null);

  // Matched existing entities (populated after loadRequest)
  matchedParty = signal<MatchedEntity | null>(null);
  matchedLegalEntity = signal<MatchedEntity | null>(null);
  matchedSite = signal<MatchedEntity | null>(null);
  unmatchedPartyName = signal<string>('');

  // Full phase list for the stepper
  allPhases = WORKFLOW_PHASES;

  // Index of the current phase (for stepper completion state)
  currentPhaseIndex = computed(() => {
    const p = this.request()?.phase || '';
    const idx = (WORKFLOW_PHASES as readonly string[]).indexOf(p);
    return idx < 0 ? 0 : idx;
  });

  // AI-extraction Material table rows (skips empty fields)
  aiTableRows = computed<AiTableRow[]>(() => {
    const r = this.request();
    if (!r) return [];
    const rows: AiTableRow[] = [];
    if (r.extractedSummary) rows.push({ label: 'Summary', value: r.extractedSummary });
    if (r.classification) {
      const pct = r.classificationConfidence ? ` (${Math.round(r.classificationConfidence * 100)}% confident)` : '';
      rows.push({ label: 'Document Type', value: r.classification + pct });
    }
    if (r.extractedEffectiveDate) rows.push({ label: 'Effective Date', value: r.extractedEffectiveDate });
    if (r.extractedExpirationDate) rows.push({ label: 'Expiration Date', value: r.extractedExpirationDate });
    if (r.extractedParties?.length) {
      const partyText = r.extractedParties
        .map(p => `${p.name}${p.role ? ' (' + p.role + ')' : ''}`)
        .join(', ');
      rows.push({ label: 'Parties', value: partyText });
    }
    if (r.fileName) rows.push({ label: 'File', value: r.fileName });
    return rows;
  });

  // ----- Document Review (approved provisions vs. the uploaded document) -----

  /** The approved provisions this request's document is reviewed against. */
  reviewProvisions = signal<Provision[]>([]);
  /** Rendered findings — the saved copy on load, the fresh run's once it completes. */
  reviewComparisons = signal<ProvisionComparison[]>([]);
  reviewAnalyzedAt = signal<string | null>(null);
  reviewRunning = signal<boolean>(false);
  reviewStatus = signal<string>('');
  reviewError = signal<string | null>(null);

  /** True once the provisions cache has been consulted, so the card can tell
   *  "still loading" from "nothing applies". */
  reviewProvisionsLoaded = signal<boolean>(false);

  /** Guards the auto-run so navigating within the same loaded request can't re-trigger it. */
  private reviewAutoRunFor: string | null = null;

  /**
   * The card stays hidden entirely when there is no document to review, or when this org
   * has no provision-analysis endpoint configured. The URL keys are read via
   * `(environment as any)`, so a missing one is not a compile error — without this guard
   * such an org would auto-run, fail, and show an error for a feature it doesn't have.
   */
  private readonly reviewEndpointConfigured = !!(environment as any).URL_PROVISION_ANALYSIS_TRIGGER;

  showDocumentReview = computed(() => this.reviewEndpointConfigured && !!this.request()?.s3FileUrl);

  /**
   * The review only runs on documents the intake AI classified as a contract.
   *
   * An approved provision is scoped by `contractTypes` — a contract-type catalog. A BAA or
   * COI has no contract type (the AI populates `baaFields` / `coiFields`, never
   * `contractFields`), so the type filter resolves to the empty string and only the
   * "All Contract Types" provisions survive it. Those would then be compared against the
   * wrong kind of document: Crestwood's Force Majeure provision graded against a
   * certificate of insurance comes back "missing, high risk" — a red finding on a document
   * that was never supposed to contain the clause. Saying "not applicable" is the honest
   * answer; a confident wrong answer is the expensive one.
   */
  reviewAppliesToDocType = computed(() => this.reviewDocType() === 'contract');

  /** What the AI decided this document is: contract | baa | coi | addendum | unknown. */
  private reviewDocType = computed(() => {
    const r = this.request();
    return (r?.classification || r?.documentType || '').toLowerCase();
  });

  /** Human label for the "review doesn't apply to this type" message. */
  reviewDocTypeLabel = computed(() => {
    switch (this.reviewDocType()) {
      case 'baa': return 'a Business Associate Agreement';
      case 'coi': return 'a Certificate of Insurance';
      case 'addendum': return 'an Addendum / Amendment';
      case 'unknown': return 'an unrecognized document type';
      case '': return 'an unrecognized document type';
      default: return `a ${this.reviewDocType()}`;
    }
  });

  reviewAnalyzedAtDisplay = computed(() => {
    const at = this.reviewAnalyzedAt();
    if (!at) return '';
    try { return new Date(at).toLocaleString(); } catch { return at; }
  });

  /**
   * The contract type used to scope the approved provisions.
   *
   * A workflow request has no saved contract, so the only type available is what the
   * intake AI read off the document. When it doesn't match a type in the catalog, the
   * scoped filter simply finds nothing and only the "All Contract Types" provisions
   * apply — which is the honest answer, not a degraded one.
   */
  private extractedAgreementType(r: WorkflowRequest | null): string {
    return r?.intakeAnalysisResult?.extraction?.contractFields?.agreementType || '';
  }

  /** What the card says it compared against, so a short list is explainable. */
  reviewScopeLabel = computed(() => {
    const count = this.reviewProvisions().length;
    if (count === 0) return '';
    const type = this.extractedAgreementType(this.request());
    const noun = count === 1 ? 'approved provision' : 'approved provisions';
    return type
      ? `${count} ${noun} applicable to ${type}`
      : `${count} ${noun}`;
  });

  /**
   * Load the applicable approved provisions for this request, then run the review if it
   * hasn't been run before. Ray's ask is for the review to be there at the START of a
   * request, so the first open runs it rather than waiting for a button — but only once:
   * the completed result is saved onto the request, and every later open renders that.
   */
  private loadDocumentReview(r: WorkflowRequest): void {
    this.reviewComparisons.set(r.provisionAnalysis?.comparisons || []);
    this.reviewAnalyzedAt.set(r.provisionAnalysis?.analyzedAt || null);
    this.reviewError.set(null);
    this.reviewProvisionsLoaded.set(false);

    if (!r.s3FileUrl || !this.reviewEndpointConfigured || !this.reviewAppliesToDocType()) return;

    this.approvedProvisionsService.applicableFor(this.extractedAgreementType(r))
      .pipe(take(1))
      .subscribe(provisions => {
        this.reviewProvisions.set(provisions);
        this.reviewProvisionsLoaded.set(true);

        const alreadyAnalyzed = (r.provisionAnalysis?.comparisons?.length || 0) > 0;
        const key = r.workflowNum || '';
        if (!alreadyAnalyzed && provisions.length > 0 && this.reviewAutoRunFor !== key) {
          this.reviewAutoRunFor = key;
          this.runDocumentReview();
        }
      });
  }

  /** Run (or re-run) the review against the request's uploaded document. */
  runDocumentReview(): void {
    const r = this.request();
    const provisions = this.reviewProvisions();
    if (!r?.workflowNum || !r.s3FileUrl || this.reviewRunning() || provisions.length === 0) return;
    if (!this.reviewAppliesToDocType()) return;

    this.reviewRunning.set(true);
    this.reviewError.set(null);
    this.reviewStatus.set('Reviewing the document against your approved provisions...');

    this.provisionAnalysisService
      .triggerProvisionAnalysis(
        r.workflowNum,
        this.approvedProvisionsService.toRequestItems(provisions),
        p => this.reviewStatus.set(p.message),
        r.s3FileUrl
      )
      .pipe(take(1))
      .subscribe((resp: ProvisionAnalysisResponse) => {
        this.reviewRunning.set(false);
        this.reviewStatus.set('');

        if (resp.status === 'complete' || resp.status === 'cached') {
          const analysis: ProvisionAnalysis = {
            analyzedAt: resp.analyzedAt || new Date().toISOString(),
            provisionSetHash: (resp as any).provisionSetHash,
            comparisons: resp.comparisons || []
          };
          this.reviewComparisons.set(analysis.comparisons);
          this.reviewAnalyzedAt.set(analysis.analyzedAt);
          this.persistDocumentReview(r, analysis);
        } else {
          this.reviewError.set(resp.error || 'The document review failed. Please try again.');
        }
      });
  }

  /**
   * Save the finished review onto the request so re-opening renders it instead of
   * re-billing an AI run. Fire-and-forget: a write failure must not blank the result
   * the user is already reading, and the backend's own cache covers the next open.
   */
  private persistDocumentReview(r: WorkflowRequest, analysis: ProvisionAnalysis): void {
    if (!r.workflowNum) return;
    this.request.set({ ...r, provisionAnalysis: analysis });
    this.workflowService.update(r.workflowNum, { provisionAnalysis: analysis })
      .pipe(take(1))
      .subscribe({
        error: err => console.error('[WorkflowItem] failed to save the document review onto the request:', err)
      });
  }

  // Sidenav actions recompute whenever the loaded request changes
  sidenavActions = computed<EntityAction[]>(() => {
    const r = this.request();
    const phase = r?.phase || '';
    const np = computeNextPhase(phase);
    const pp = computePreviousPhase(phase);
    const nextLabel = np ? `Advance to ${this.phaseShortName(np)}` : 'Advance';
    const prevLabel = pp ? `Return to ${this.phaseShortName(pp)}` : 'Return';
    return [
      { label: nextLabel, icon: 'arrow_forward', action: () => this.advance(), disabled: !np || !r },
      { label: prevLabel, icon: 'arrow_back',   action: () => this.retreat(), disabled: !pp || !r },
      { label: 'New Workflow Request', icon: 'add_circle', action: () => this.openNewWorkflowRequestDialog(), disabled: false },
      { label: 'Edit',         icon: 'edit', action: () => this.editRequest(), disabled: !r },
      { label: 'Back to List', icon: 'list', action: () => this.router.navigate(['/securehome/workflows']), disabled: false }
    ];
  });

  ngOnInit(): void {
    this.sidenavContentService.registerDetailSidenavToggle(() => {
      if (this.actionsSidenav) this.actionsSidenav.toggle();
    });

    // AuthService.getUserEmail() is the SSO-safe resolver (decodes the Cognito ID
    // token under the hosted-UI code flow); resolved once up front so Advance /
    // Return don't have to await it mid-click.
    this.authService.getUserEmail()
      .then(email => { this.currentUserEmail = email ?? ''; })
      .catch(e => console.warn('[WorkflowItem] failed to resolve current user', e));

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.loadRequest(params['id']);
      }
    });
  }

  ngOnDestroy(): void {
    this.sidenavContentService.unregisterDetailSidenavToggle();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ----- Loading -----

  private loadRequest(workflowNum: string): void {
    this.loading.set(true);
    this.workflowService.get(workflowNum).subscribe({
      next: (r) => {
        this.request.set(r);
        this.loading.set(false);
        this.resolveMatchedEntities(r);
        this.loadDocumentReview(r);
      },
      error: () => {
        this.error.set('Error loading workflow request');
        this.loading.set(false);
      }
    });
  }

  private resolveMatchedEntities(r: WorkflowRequest | null): void {
    this.matchedParty.set(null);
    this.matchedLegalEntity.set(null);
    this.matchedSite.set(null);
    this.unmatchedPartyName.set('');
    if (!r?.extractedParties?.length) return;

    // First vendor-role party drives party + site lookup; first facility-role party drives legal entity.
    const vendorParty = r.extractedParties.find(p => p.role === 'vendor') || r.extractedParties[0];
    const facilityParty = r.extractedParties.find(p => p.role === 'facility');

    if (vendorParty?.name) {
      this.partyService.allParties$.pipe(take(1)).subscribe(parties => {
        const match = this.findByName(parties, vendorParty.name, ['name']);
        if (match) {
          this.matchedParty.set({
            id: match.vendorNum || '',
            name: match.name || vendorParty.name,
            route: '/securehome/vendor/' + (match.vendorNum || '')
          });
        } else {
          this.unmatchedPartyName.set(vendorParty.name);
        }
      });
    }

    const legalEntityName = facilityParty?.name || vendorParty?.name;
    if (legalEntityName) {
      this.legalEntityService.allLegalEntities$.pipe(take(1)).subscribe(entities => {
        const match = this.findByName(entities, legalEntityName, ['legalEntityName']);
        if (match) {
          this.matchedLegalEntity.set({
            id: match.legalEntityNum || '',
            name: match.legalEntityName || legalEntityName,
            route: '/securehome/legal-entity/' + (match.legalEntityNum || '')
          });
        }
      });
    }

    // Site lookup uses the vendor party name as a best-effort; often AI won't extract a
    // site distinctly. Still surface a match if the name happens to align.
    if (vendorParty?.name) {
      this.siteService.allSites$.pipe(take(1)).subscribe(sites => {
        const match = this.findByName(sites, vendorParty.name, ['name']);
        if (match) {
          this.matchedSite.set({
            id: match.siteNum || '',
            name: match.name || vendorParty.name,
            route: '/securehome/site/' + (match.siteNum || '')
          });
        }
      });
    }
  }

  /** Exact (case-insensitive) match first, then case-insensitive "contains". */
  private findByName<T extends Record<string, any>>(list: T[], name: string, fields: string[]): T | null {
    if (!list?.length || !name) return null;
    const lower = name.toLowerCase().trim();
    for (const item of list) {
      for (const f of fields) {
        if ((item?.[f] || '').toLowerCase().trim() === lower) return item;
      }
    }
    for (const item of list) {
      for (const f of fields) {
        const v = (item?.[f] || '').toLowerCase();
        if (v && (v.includes(lower) || lower.includes(v))) return item;
      }
    }
    return null;
  }

  // ----- Phase advancement -----

  /**
   * Who to stamp on the phase change. The backend writes this into
   * `phaseHistory.enteredBy`, so it must be the person CLICKING the button — the
   * original requestor was previously used, which credited every phase move in the
   * audit trail to whoever opened the request rather than whoever advanced it.
   * Falls back to the requestor only if the signed-in user can't be resolved.
   */
  private actingUser(r: WorkflowRequest): string {
    return this.currentUserEmail || this.getPersonEmail(r.requestor) || 'unknown@example.com';
  }

  advance(): void {
    const r = this.request();
    if (!r?.workflowNum || !r.phase) return;
    this.workflowService.advancePhase(r.workflowNum, r.phase, this.actingUser(r)).subscribe({
      next: res => { if (res && r.workflowNum) this.loadRequest(r.workflowNum); }
    });
  }

  retreat(): void {
    const r = this.request();
    if (!r?.workflowNum || !r.phase) return;
    this.workflowService.retreatPhase(r.workflowNum, r.phase, this.actingUser(r)).subscribe({
      next: res => { if (res && r.workflowNum) this.loadRequest(r.workflowNum); }
    });
  }

  // ----- Display helpers -----

  /** "Phase I - Requested" → "Requested" for compact stepper labels and button text. */
  phaseShortName(phase: string | undefined | null): string {
    if (!phase) return '';
    return phase.replace(/^Phase [IVX]+\s*-\s*/, '').trim();
  }

  getPersonName(person: any): string {
    if (!person) return '—';
    if (typeof person === 'string') return person;
    return person.friendlyName || person.email || '—';
  }

  private getPersonEmail(person: any): string {
    if (!person) return '';
    if (typeof person === 'string') return person;
    return person.email || '';
  }

  viewMatched(target: MatchedEntity | null): void {
    if (!target || !target.id) return;
    this.router.navigate([target.route]);
  }

  viewFile(): void {
    const url = this.request()?.s3FileUrl;
    if (url) window.open(url, '_blank');
  }

  // ----- Sidenav action callbacks -----

  editRequest(): void {
    const num = this.request()?.workflowNum;
    if (num) this.router.navigate(['/securehome/new-workflow', num]);
  }

  openNewWorkflowRequestDialog(): void {
    this.dialog.open(NewWorkflowRequestDialogComponent, {
      width: '640px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: false,
      data: { mode: 'workflow' }
    });
  }
}
